import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { PieChart, Pie, Cell, Sector } from 'recharts';
import uuid from 'react-uuid';
import classNames from 'classnames';
import { useMediaQuery } from '../../common/hooks/useMediaQuery';

function AnalyticsPieChart({ data, label, count }) {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [activePie, setActivePie] = useState(null);

  const dataTotal = useMemo(() => {
    if (!data?.length) {
      return 0;
    }

    const result = data.reduce((acc, el) => (el?.value || 0) + acc, 0);
    return result <= 0 ? 0 : result;
  }, [data]);

  const renderActiveShape = useCallback(
    ({ cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill }) => (
      <g>
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius + 2}
          startAngle={startAngle + 2}
          endAngle={endAngle - 2}
          fill={fill}
          onMouseLeave={onPieLeave}
        />
      </g>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activePie],
  );

  const onPieOver = useCallback((_, index) => {
    setActivePie({
      index,
      name: _?.name,
      value: _?.value,
      color: _?.fill,
    });
  }, []);

  const onPieEnter = (_, index) => {
    setActivePie({
      index,
      name: _?.name,
      value: _?.value,
      color: _?.fill,
    });
  };

  const onPieLeave = useCallback(() => {
    setActivePie(null);
  }, []);

  const isActive = (name) =>
    activePie && activePie?.name?.toLowerCase() === name.toLowerCase();

  const FALLBACK_COLOUR = '#252153';
  const EMPTY_STATE_COLOUR = '#D2D2D5';

  const isEmptyChart = dataTotal === 0;

  const chartData = useMemo(() => {
    if (isEmptyChart) {
      return [{ name: 'No Data', value: 1 }];
    }

    return data.map((el) => ({
      name: el?.name || '',
      value: el?.value || 0,
      type: el?.type,
    }));
  }, [data, isEmptyChart]);

  const CHART_SIZE = isMobile ? 260 : 380;
  const CHART_INNER_RADIUS = isMobile ? 60 : 100;
  const CHART_OUTER_RADIUS = isMobile ? 120 : 180;

  if (!data.length) {
    return null;
  }

  return (
    <div className="analytics-pie-chart">
      <div className="analytics-pie-chart-labels">
        <div className="analytics-pie-chart__legend">
          {data.map((el) => (
            <div key={el.type} className="analytics-pie-chart__legend-label">
              <div
                className="legend-label__color"
                style={{ backgroundColor: el?.color || FALLBACK_COLOUR }}
              />
              <p className={classNames('m-0', isActive(el?.name || '-') && 'fw-bold')}>
                <span>{el?.name || '-'}</span>
                <span className="ms-1 fw-bold">{el?.value || 0}</span>
              </p>
            </div>
          ))}
        </div>
      </div>
      <div className="analytics-pie-chart-column">
        <PieChart width={CHART_SIZE} height={CHART_SIZE}>
          <Pie
            data={chartData}
            innerRadius={CHART_INNER_RADIUS}
            outerRadius={CHART_OUTER_RADIUS}
            fill="#8884d8"
            paddingAngle={0}
            dataKey="value"
            activeIndex={!isEmptyChart ? activePie?.index || null : null}
            activeShape={!isEmptyChart ? renderActiveShape : undefined}
            onMouseEnter={!isEmptyChart ? onPieEnter : undefined}
            onMouseOver={!isEmptyChart ? onPieOver : undefined}
            onMouseLeave={!isEmptyChart ? onPieLeave : undefined}
          >
            {!isEmptyChart
              ? data.map((el) => <Cell key={uuid()} fill={el?.color || FALLBACK_COLOUR} />)
              : [<Cell key="empty" fill={EMPTY_STATE_COLOUR} />]}
          </Pie>
          <text
            x="50%"
            y="50%"
            textAnchor="middle"
            dominantBaseline="middle"
            className="analytics-pie-chart__count-label"
          >
            {count}
          </text>
          <text
            x="50%"
            y="60%"
            textAnchor="middle"
            dominantBaseline="middle"
            className="analytics-pie-chart__status-label"
          >
            {label}
          </text>
        </PieChart>
        {!dataTotal ? <div className="analytics-pie-chart__empty" /> : ''}
      </div>
    </div>
  );
}

AnalyticsPieChart.propTypes = {
  data: PropTypes.array.isRequired,
  label: PropTypes.string.isRequired,
  count: PropTypes.number.isRequired,
};

export default AnalyticsPieChart;
